'use client';
import { useRef, ReactNode, useEffect, useState, useCallback, memo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { usePageScroll } from '../page-scroll-provider/page-scroll-provider';

type FadeOnScrollProps = {
    children: ReactNode;
};

export const FadeOnScroll = memo(({ children }: FadeOnScrollProps) => {
    const prefersReducedMotion = useReducedMotion();
    const shouldReduceMotion = prefersReducedMotion === true;

    // Skip fade effect for reduced motion
    if (shouldReduceMotion) {
        return <div>{children}</div>;
    }

    return <FadeOnScrollInner>{children}</FadeOnScrollInner>;
});

const FadeOnScrollInner = memo(({ children }: FadeOnScrollProps) => {
    const elementRef = useRef<HTMLDivElement>(null);
    const pageScroll = usePageScroll();
    const [opacity, setOpacity] = useState(1);
    const rafRef = useRef<number | null>(null);
    const lastOpacityRef = useRef(1);

    const updateOpacity = useCallback(() => {
        if (!elementRef.current || !pageScroll) {
            return;
        }

        const rect = elementRef.current.getBoundingClientRect();
        const newOpacity = pageScroll.calculateFadeOpacity(rect);

        // Only update state if opacity changed significantly
        if (Math.abs(newOpacity - lastOpacityRef.current) > 0.01) {
            lastOpacityRef.current = newOpacity;
            setOpacity(newOpacity);
        }
    }, [pageScroll]);

    useEffect(() => {
        if (!pageScroll) return;

        const handleScroll = () => {
            if (rafRef.current) return; // Skip if already scheduled
            rafRef.current = requestAnimationFrame(() => {
                updateOpacity();
                rafRef.current = null;
            });
        };

        // Initial calculation
        updateOpacity();

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
            }
        };
    }, [updateOpacity, pageScroll]);

    return (
        <motion.div ref={elementRef} animate={{ opacity }} transition={{ duration: 0.15 }}>
            {children}
        </motion.div>
    );
});

FadeOnScroll.displayName = 'FadeOnScroll';
FadeOnScrollInner.displayName = 'FadeOnScrollInner';

export default FadeOnScroll;
